import { useQueries, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import StatCard from "@/components/dashboard/StatCard";
import EmailRefreshCard from "@/components/dashboard/EmailRefreshCard";
import RecentActivity from "@/components/dashboard/RecentActivity";
import { ActiveTasksCard } from "@/components/dashboard/ActiveTasksCard";
import { usePlayerStats } from "@/hooks/usePlayerStats";
import { useDataCoordinator } from "@/hooks/useDataCoordinator";
import { Player, Email } from "@/lib/types";
import { useMemo, useEffect, useState, useRef } from "react";
import { StatCardSkeleton } from "@/components/ui/SkeletonLoader";
import { apiRequest } from "@/lib/queryClient";
import { PaymentModal } from "@/components/payment/PaymentModal";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { TeamStatusCard } from "@/components/team/team-status-card";
import { TeamEmailAccounts } from "@/components/team/team-email-accounts";
import TeamInvitationPopup from "@/components/team-invitation-popup";

export default function Dashboard() {
  const { user } = useAuth();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const retryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [userTeam, setUserTeam] = useState<any>(null);
  const { toast } = useToast();
  
  // Check if we have a valid session token
  const hasSessionToken = !!localStorage.getItem('sessionId');
  
  // Check if user has a team
  useEffect(() => {
    const checkUserTeam = async () => {
      try {
        const response = await api.get('/api/teams/my-team');
        if (response.data) {
          setUserTeam(response.data);
        }
      } catch (error) {
        // User doesn't have a team, that's okay
      }
    };
    
    if (user) {
      checkUserTeam();
    }
  }, [user]);
  
  // Check subscription status only if we have a valid session token
  const { data: subscriptionStatus, error: subscriptionError, refetch: refetchSubscription } = useQuery({
    queryKey: ['/api/payments/subscription-status'],
    queryFn: async () => {
      const response = await api.get('/api/payments/subscription-status');
      return response.data;
    },
    enabled: !!user && hasSessionToken,
    refetchInterval: false,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache this data
    retry: 1, // Only retry once to avoid long delays
  });
  
  // Check if payment is successful from URL params or referrer
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    // Check if we're coming from Stripe (payment link redirect)
    const referrer = document.referrer;
    const isFromStripe = referrer.includes('stripe.com') || referrer.includes('checkout.stripe.com');
    
    // Also check sessionStorage for a payment in progress flag
    const paymentInProgress = sessionStorage.getItem('paymentInProgress');
    
    if ((paymentStatus === 'success' && sessionId) || isFromStripe || paymentInProgress === 'true') {
      // Remove the query parameters
      window.history.replaceState({}, '', window.location.pathname);
      
      // Payment is being processed
      
      // Start retry mechanism
      setRetryCount(0);
      
      // Set up retry interval - check every 3 seconds for up to 30 seconds
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
      }
      
      retryIntervalRef.current = setInterval(async () => {
        setRetryCount(prev => {
          const newCount = prev + 1;
          
          // After 10 retries (30 seconds), stop retrying
          if (newCount >= 10) {
            if (retryIntervalRef.current) {
              clearInterval(retryIntervalRef.current);
              retryIntervalRef.current = null;
            }
            
            // Stop retrying after timeout
            
            return prev;
          }
          
          return newCount;
        });
        
        // Refetch subscription status
        const result = await refetchSubscription();
        
        if (result.data?.has_active_subscription) {
          // Success! Clear the interval and show success message
          if (retryIntervalRef.current) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
          }
          
          toast({
            title: "Subscription activated!",
            description: "Welcome to Bloom! Your subscription is now active.",
            variant: "default",
          });
          
          // Hide payment modal
          setShowPaymentModal(false);
          
          // Clear payment in progress flag
          sessionStorage.removeItem('paymentInProgress');
          
          // Reload to ensure all data is fresh
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      }, 3000); // Check every 3 seconds
    }
    
    // Cleanup on unmount
    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
      }
      // Clear payment in progress flag on unmount
      sessionStorage.removeItem('paymentInProgress');
    };
  }, [refetchSubscription, toast]);
  
  // Handle subscription status check or show payment modal immediately if no session
  useEffect(() => {
    if (!user) {
      return; // Wait for user to load
    }
    
    // Check if user is coming from pricing page (back button scenario)
    const urlParams = new URLSearchParams(window.location.search);
    const fromPricing = urlParams.get('from') === 'pricing';
    
    if (!hasSessionToken) {
      // User exists but no session token - likely needs email confirmation and payment
      setIsCheckingSubscription(false);
      setShowPaymentModal(true);
      
      // Clean up URL if coming from pricing
      if (fromPricing) {
        window.history.replaceState({}, '', window.location.pathname);
      }
      return;
    }
    
    // We have a session token, check subscription status
    if (subscriptionStatus !== undefined) {
      setIsCheckingSubscription(false);
      if (!subscriptionStatus?.has_active_subscription) {
        setShowPaymentModal(true);
      }
      
      // Clean up URL if coming from pricing
      if (fromPricing) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    } else if (subscriptionError) {
      // If there's an error checking subscription status (like auth failure),
      // assume payment is required and show the modal
      console.log('Subscription status check failed, showing payment modal:', subscriptionError);
      setIsCheckingSubscription(false);
      setShowPaymentModal(true);
      
      // Clean up URL if coming from pricing
      if (fromPricing) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [user, hasSessionToken, subscriptionStatus, subscriptionError]);
  
  // Use data coordinator for unified loading management
  const {
    shortlists,
    emails,
    isDataReady,
    hasErrors,
    errors
  } = useDataCoordinator();
  
  // Get players statistics using our custom hook
  const { 
    totalPlayers, 
    newPlayersThisWeek, 
    percentIncreaseFromLastMonth, 
    timeSaved, 
    isLoading: isLoadingStats 
  } = usePlayerStats();
  
  // Data is now provided by the data coordinator above
  
  // Use useQueries to fetch all shortlist players in parallel
  const shortlistQueryConfig = useMemo(() => 
    shortlists
      .filter((shortlist) => shortlist.user_id === user?.id)
      .map((shortlist) => ({
        queryKey: [`/api/shortlists/${shortlist.id}`],
        queryFn: async () => {
          const response = await apiRequest("GET", `/api/shortlists/${shortlist.id}`);
          if (!response.ok) {
            throw new Error(`Failed to fetch shortlist ${shortlist.id}`);
          }
          return response.json();
        },
        staleTime: 10000, // 10 seconds
        enabled: !!user && !!shortlist.id,
      })),
    [shortlists, user?.id]
  );

  const shortlistQueries = useQueries({
    queries: shortlistQueryConfig,
  });
  
  // Calculate unique shortlisted players from all shortlist queries
  const uniqueShortlistedPlayers = useMemo(() => {
    const playerIds = new Set<number>();
    
    shortlistQueries.forEach((query) => {
      if (query.data?.players && Array.isArray(query.data.players)) {
        query.data.players.forEach((player: Player) => {
          playerIds.add(player.id);
        });
      }
    });
    
    return playerIds.size;
  }, [
    shortlistQueries.length,
    // Create a stable string representation of data existence
    shortlistQueries.map(q => !!q.data).join(',')
  ]);
  
  // Format time saved as hours and minutes
  const formatTimeSaved = () => {
    const hours = Math.floor(timeSaved);
    const minutes = Math.round((timeSaved - hours) * 60);
    
    return `${hours}h ${minutes}m`;
  };
  
  // Calculate percentage of user's database in shortlists
  const calculateShortlistPercentage = (): number => {
    if (!totalPlayers) return 0;
    
    return Math.round((uniqueShortlistedPlayers / totalPlayers) * 100);
  };
  
  // Calculate email open rate
  const calculateEmailOpenRate = (): number => {
    if (!emails.length) return 0;
    
    const openedEmails = emails.filter((email: Email) => email.opened);
    return Math.round((openedEmails.length / emails.length) * 100);
  };
  
  // Show error state if there are critical errors
  if (hasErrors && !isDataReady) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-500 mb-2">
            <i className="fas fa-exclamation-triangle text-3xl"></i>
          </div>
          <h3 className="text-lg font-medium text-red-900 mb-2">Failed to load dashboard</h3>
          <p className="text-red-700 mb-4">
            {errors.join(', ')}
          </p>
          <div className="flex justify-center gap-3">
            <button 
              onClick={() => window.location.reload()} 
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Retry
            </button>
            <button 
              onClick={() => {
                localStorage.removeItem("sessionId");
                localStorage.removeItem("user");
                window.location.href = "/auth/login";
              }} 
              className="bg-neutral-600 text-white px-4 py-2 rounded hover:bg-neutral-700"
            >
              Re-login
            </button>
          </div>
          <p className="text-xs text-red-600 mt-3">
            If this problem persists, try logging out and logging back in.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while checking subscription
  if (isCheckingSubscription) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 relative">
      {/* Subtle gradient overlay for extra visual depth */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: `
            radial-gradient(circle at 20% 30%, rgba(6, 182, 212, 0.05) 0%, transparent 40%),
            radial-gradient(circle at 80% 70%, rgba(16, 185, 129, 0.05) 0%, transparent 40%)
          `
        }}
      />
      
      <div className="space-y-8 relative z-10">
        {/* Summary Stats */}
        <div className="mb-6 bg-white/40 backdrop-blur-sm rounded-xl p-6 border border-teal-100">
          <h1 className="text-2xl font-semibold bg-gradient-to-r from-cyan-600 to-emerald-600 bg-clip-text text-transparent">
            Welcome back, {user?.name?.split(' ')[0] || 'Coach'}
          </h1>
          <p className="text-sm text-gray-600">Here's what's happening with your scouting today</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 sm:gap-6 mb-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
          {isLoadingStats ? (
            // Show skeleton loading for stats
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              {/* Time Saved Card */}
              <StatCard
                title="Time Saved"
                value={formatTimeSaved()}
                icon="fas fa-clock"
                iconBgColor="bg-primary bg-opacity-10"
                iconColor="text-primary"
                progressColor="bg-primary"
                progressValue={Math.min(Math.abs(percentIncreaseFromLastMonth), 100)}
                subtext={`${percentIncreaseFromLastMonth}% ${percentIncreaseFromLastMonth >= 0 ? 'increase' : 'decrease'} from last month`}
              />

              {/* Players Database Card */}
              <StatCard
                title="Player Database"
                value={`${totalPlayers} Players`}
                icon="fas fa-database"
                iconBgColor="bg-primary bg-opacity-10"
                iconColor="text-primary"
                progressColor="bg-primary"
                progressValue={Math.min(Math.max(newPlayersThisWeek, 0), totalPlayers) / Math.max(totalPlayers, 1) * 100}
                subtext={`+${newPlayersThisWeek} new players this week`}
              />

              {/* Shortlisted Players Card */}
              <StatCard
                title="Shortlisted"
                value={`${uniqueShortlistedPlayers} Players`}
                icon="fas fa-star"
                iconBgColor="bg-primary bg-opacity-10"
                iconColor="text-primary"
                progressColor="bg-primary"
                progressValue={calculateShortlistPercentage()}
                subtext={`${calculateShortlistPercentage()}% of your database`}
              />

              {/* Email Responses Card */}
              <StatCard
                title="Email Responses"
                value={`${emails.filter((e: Email) => e.replied).length} Replies`}
                icon="fas fa-envelope"
                iconBgColor="bg-blue-100"
                iconColor="text-blue-600"
                progressColor="bg-blue-600"
                progressValue={calculateEmailOpenRate()}
                subtext={`${calculateEmailOpenRate()}% open rate`}
              />
            </>
          )}
        </div>

        {/* Team Status Card */}
        <div className="mb-6">
          <TeamStatusCard />
        </div>

        {/* Email Refresh & Recent Activity Section */}
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
          {/* Email Refresh Card */}
          <EmailRefreshCard isDataReady={isDataReady} />

          {/* Recent Activity Section */}
          <RecentActivity />
        </div>

        {/* Active Background Tasks */}
        <ActiveTasksCard />
        
        {/* Team Email Accounts - Show only if user is part of a team */}
        {userTeam && (
          <div className="mt-6">
            <TeamEmailAccounts />
          </div>
        )}
      </div>
      
      {/* Payment Modal */}
      <PaymentModal 
        open={showPaymentModal} 
        onOpenChange={(open) => {
          // Don't allow closing the modal if user has no active subscription
          // Only allow closing if they actually have a subscription
          if (!open && subscriptionStatus?.has_active_subscription) {
            setShowPaymentModal(false);
          }
        }}
      />
      
      {/* Team Invitation Popup */}
      <TeamInvitationPopup />
    </div>
  );
}